import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputNumberModule } from 'primeng/inputnumber';
import { DropdownModule } from 'primeng/dropdown';
import { ProjectionFieldDef } from '../../../constants/projection-field-definitions';

@Component({
  selector: 'td[appProjectionFieldCell]',
  standalone: true,
  imports: [CommonModule, FormsModule, InputNumberModule, DropdownModule],
  template: `
    <!-- Currency / Number -->
    <ng-container *ngIf="isCurrencyOrNumber">
      <span *ngIf="!isEditable" class="display-value">
        {{formatCurrency(value)}}
      </span>
      <p-inputNumber *ngIf="isEditable"
                     [ngModel]="value"
                     (ngModelChange)="onValueChanged($event)"
                     [mode]="field.fieldType === 'currency' ? 'currency' : 'decimal'"
                     currency="USD"
                     [useGrouping]="field.fieldType === 'currency'"
                     [minFractionDigits]="0"
                     [maxFractionDigits]="0"
                     placeholder="0"
                     [inputStyleClass]="field.fieldType === 'number' ? 'cell-input number-input' : 'cell-input'">
      </p-inputNumber>
    </ng-container>

    <!-- Dropdown -->
    <ng-container *ngIf="field.fieldType === 'dropdown'">
      <span *ngIf="!isEditable" class="display-value dropdown-display">
        {{dropdownValue || '--'}}
      </span>
      <p-dropdown *ngIf="isEditable"
                  [options]="dropdownOptions"
                  [ngModel]="value"
                  (ngModelChange)="onValueChanged($event)"
                  placeholder="--"
                  [appendTo]="'body'"
                  styleClass="cell-dropdown">
      </p-dropdown>
    </ng-container>

    <!-- Boolean -->
    <ng-container *ngIf="field.fieldType === 'boolean'">
      <span *ngIf="!isEditable" class="display-value">
        {{getBooleanDisplay(value)}}
      </span>
      <p-dropdown *ngIf="isEditable"
                  [options]="booleanOptions"
                  [ngModel]="value"
                  (ngModelChange)="onValueChanged($event)"
                  [appendTo]="'body'"
                  styleClass="cell-dropdown boolean-dropdown">
      </p-dropdown>
    </ng-container>
  `,
  styles: [`
    :host {
      display: table-cell;
    }

    .display-value {
      color: #333;
      font-size: 12px;
      text-align: right;
      display: block;
      width: 100%;
    }
    .display-value.dropdown-display {
      text-align: left;
    }

    /* Compact PrimeNG inputs for 36px grid rows */
    :host ::ng-deep {
      /* ── InputNumber ── */
      .p-inputnumber {
        width: 100%;
      }
      .p-inputnumber .p-inputtext,
      .cell-input {
        padding: 4px 8px;
        font-size: 12px;
        text-align: right;
        height: 28px;
        width: 100%;
        border: 1px solid #dee2e6 !important;
        border-radius: 3px;
        background: #fff !important;
        color: #333 !important;
      }
      .p-inputnumber .p-inputtext:focus,
      .cell-input:focus {
        border-color: #1570EF !important;
        outline: none;
        box-shadow: 0 0 0 2px rgba(21, 112, 239, 0.15) !important;
      }
      .cell-input.number-input {
        text-align: center;
        width: 80px;
      }

      /* Hide number spinners */
      .p-inputnumber-button-group { display: none; }
      .p-inputtext::-webkit-inner-spin-button,
      .p-inputtext::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }

      /* ── Dropdown (PrimeNG v18 uses p-select classes) ── */
      .p-select,
      .p-dropdown {
        width: 100%;
        height: 28px;
        border: 1px solid #dee2e6 !important;
        border-radius: 3px;
        background: #fff !important;
        color: #333 !important;
      }
      .p-select-label,
      .p-dropdown-label {
        padding: 3px 8px !important;
        font-size: 12px;
        line-height: 22px;
        color: #333 !important;
        background: transparent !important;
      }
      .p-select-dropdown,
      .p-dropdown-trigger {
        width: 28px;
        color: #666 !important;
        background: transparent !important;
      }
      .boolean-dropdown {
        width: 80px;
      }
    }
  `]
})
export class ProjectionFieldCellComponent {
  @Input() field: ProjectionFieldDef;
  @Input() scenarioType: string = '';
  @Input() value: number = 0;
  @Input() dropdownValue: string = '';

  @Output() valueChange = new EventEmitter<{ fieldKey: string, value: any }>();

  booleanOptions = [
    { label: 'No', value: 0 },
    { label: 'Yes', value: 1 }
  ];

  get isEditable(): boolean {
    return !this.field?.isSubtotal && this.scenarioType === 'scenario';
  }

  get isCurrencyOrNumber(): boolean {
    return this.field?.fieldType === 'currency' || this.field?.fieldType === 'number';
  }

  get dropdownOptions(): { label: string, value: string }[] {
    return (this.field?.dropdownOptions || []).map(opt => ({ label: opt, value: opt }));
  }

  formatCurrency(val: number): string {
    if (val == null || val === 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(val);
  }

  getBooleanDisplay(val: number): string {
    return val ? 'Yes' : 'No';
  }

  onValueChanged(newValue: any): void {
    this.valueChange.emit({ fieldKey: this.field.key, value: newValue });
  }
}
